import { useState, useCallback, useMemo } from 'react';

export interface UseDialogStateReturn {
  // Select All Dialog
  showSelectAllDialog: boolean;
  setShowSelectAllDialog: (show: boolean) => void;
  selectAllDialogData: {
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null;
  setSelectAllDialogData: (data: any) => void;

  // Functional Area Switch Confirmation Dialog
  showSwitchConfirmDialog: boolean;
  setShowSwitchConfirmDialog: (show: boolean) => void;
  pendingSwitchToArea: string;
  setPendingSwitchToArea: (area: string) => void;

  // Cancel Confirmation Dialog
  showCancelConfirmDialog: boolean;
  setShowCancelConfirmDialog: (show: boolean) => void;

  // Submit Confirmation Dialog
  showSubmitConfirmDialog: boolean;
  setShowSubmitConfirmDialog: (show: boolean) => void;
  submitChangesViewMode: 'per-app' | 'per-lead';
  setSubmitChangesViewMode: (mode: 'per-app' | 'per-lead') => void;

  // Submission Review Dialog
  showSubmissionReview: boolean;
  setShowSubmissionReview: (show: boolean) => void;

  // Changes Review Dialog
  showChangesReviewDialog: boolean;
  setShowChangesReviewDialog: (show: boolean) => void;

  // Dialog handlers
  closeSelectAllDialog: () => void;
  closeSwitchConfirmDialog: () => void;
  closeCancelConfirmDialog: () => void;
  closeSubmitConfirmDialog: () => void;
  closeSubmissionReview: () => void;
  closeChangesReviewDialog: () => void;
}

/**
 * Custom hook to manage dialog state
 * @returns Dialog state and handlers
 */
export const useDialogState = (): UseDialogStateReturn => {
  // Select All Dialog
  const [showSelectAllDialog, setShowSelectAllDialog] =
    useState<boolean>(false);
  const [selectAllDialogData, setSelectAllDialogData] = useState<{
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null>(null);

  // Functional Area Switch Confirmation Dialog
  const [showSwitchConfirmDialog, setShowSwitchConfirmDialog] =
    useState<boolean>(false);
  const [pendingSwitchToArea, setPendingSwitchToArea] = useState<string>('');

  // Cancel Confirmation Dialog
  const [showCancelConfirmDialog, setShowCancelConfirmDialog] =
    useState<boolean>(false);

  // Submit Confirmation Dialog
  const [showSubmitConfirmDialog, setShowSubmitConfirmDialog] =
    useState<boolean>(false);
  const [submitChangesViewMode, setSubmitChangesViewMode] = useState<
    'per-app' | 'per-lead'
  >('per-app');

  // Submission Review Dialog
  const [showSubmissionReview, setShowSubmissionReview] =
    useState<boolean>(false);

  // Changes Review Dialog
  const [showChangesReviewDialog, setShowChangesReviewDialog] =
    useState<boolean>(false);

  // Close dialog handlers
  const closeSelectAllDialog = useCallback(() => {
    setShowSelectAllDialog(false);
    setSelectAllDialogData(null);
  }, []);

  const closeSwitchConfirmDialog = useCallback(() => {
    setShowSwitchConfirmDialog(false);
    setPendingSwitchToArea('');
  }, []);

  const closeCancelConfirmDialog = useCallback(() => {
    setShowCancelConfirmDialog(false);
  }, []);

  const closeSubmitConfirmDialog = useCallback(() => {
    setShowSubmitConfirmDialog(false);
  }, []);

  const closeSubmissionReview = useCallback(() => {
    setShowSubmissionReview(false);
  }, []);

  const closeChangesReviewDialog = useCallback(() => {
    setShowChangesReviewDialog(false);
  }, []);

  return useMemo(
    () => ({
      // Select All Dialog
      showSelectAllDialog,
      setShowSelectAllDialog,
      selectAllDialogData,
      setSelectAllDialogData,

      // Functional Area Switch Confirmation Dialog
      showSwitchConfirmDialog,
      setShowSwitchConfirmDialog,
      pendingSwitchToArea,
      setPendingSwitchToArea,

      // Cancel Confirmation Dialog
      showCancelConfirmDialog,
      setShowCancelConfirmDialog,

      // Submit Confirmation Dialog
      showSubmitConfirmDialog,
      setShowSubmitConfirmDialog,
      submitChangesViewMode,
      setSubmitChangesViewMode,

      // Submission Review Dialog
      showSubmissionReview,
      setShowSubmissionReview,

      // Changes Review Dialog
      showChangesReviewDialog,
      setShowChangesReviewDialog,

      // Dialog handlers
      closeSelectAllDialog,
      closeSwitchConfirmDialog,
      closeCancelConfirmDialog,
      closeSubmitConfirmDialog,
      closeSubmissionReview,
      closeChangesReviewDialog,
    }),
    [
      showSelectAllDialog,
      setShowSelectAllDialog,
      selectAllDialogData,
      setSelectAllDialogData,
      showSwitchConfirmDialog,
      setShowSwitchConfirmDialog,
      pendingSwitchToArea,
      setPendingSwitchToArea,
      showCancelConfirmDialog,
      setShowCancelConfirmDialog,
      showSubmitConfirmDialog,
      setShowSubmitConfirmDialog,
      submitChangesViewMode,
      setSubmitChangesViewMode,
      showSubmissionReview,
      setShowSubmissionReview,
      showChangesReviewDialog,
      setShowChangesReviewDialog,
      closeSelectAllDialog,
      closeSwitchConfirmDialog,
      closeCancelConfirmDialog,
      closeSubmitConfirmDialog,
      closeSubmissionReview,
      closeChangesReviewDialog,
    ],
  );
};
